import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { SignInButton, UserButton } from '@clerk/clerk-react';
import { getClerkConfig } from '../utils/clerkConfigLoader';
import BoardView from './BoardView';
import SpeechToTask from './SpeechToTask';
import BackendOfflineNotice from './components/BackendOfflineNotice';
import PersonalBoard from './PersonalBoard';
import * as taskService from './services/taskService';
import BackendStatus from './components/BackendStatus';
import config from '../config';

// Color palette for task highlights - more refined palette
export const TASK_COLORS = {
  blue: '#007AFF',     // Apple blue
  red: '#FF3B30',      // Apple red
  green: '#34C759',    // Apple green
  yellow: '#FFCC00',   // Apple yellow
  purple: '#AF52DE',   // Apple purple
  teal: '#5AC8FA',     // Apple teal
  orange: '#FF9500',   // Apple orange
  pink: '#FF2D55',     // Apple pink
  gray: '#8E8E93'      // Apple gray
};

// Get a random color key from the TASK_COLORS object
export const getRandomColorKey = () => {
  const colorKeys = Object.keys(TASK_COLORS);
  return colorKeys[Math.floor(Math.random() * colorKeys.length)];
};

// Styled components for the task manager UI
const TaskManagerContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #F5F5F7;  /* Apple light background */
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  color: #1D1D1F;  /* Apple dark text */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`;

const Header = styled.header`
  background-color: rgba(255, 255, 255, 0.8);  /* Semi-transparent white */
  backdrop-filter: blur(10px);  /* Apple-style blur */
  -webkit-backdrop-filter: blur(10px);
  color: #1D1D1F;
  padding: 1.25rem 2rem;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.08);  /* Subtle shadow */
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: -0.02em;  /* Apple-style tight letter spacing */
`;

const MainContent = styled.main`
  flex: 1;
  padding: 2rem;
  overflow: auto;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
`;

const SectionTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: #1D1D1F;
  position: relative;
  letter-spacing: -0.02em;
`;

const BoardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2.5rem;
`;

const BoardCard = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);  /* Subtle shadow */
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);  /* Smooth animation */
  border: 1px solid rgba(0, 0, 0, 0.03);
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 20px rgba(0, 0, 0, 0.08);
  }
`;

const BoardTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1D1D1F;
  letter-spacing: -0.01em;
`;

const BoardStats = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: #86868B;  /* Apple secondary text color */
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  margin-right: 1rem;
  
  svg {
    margin-right: 0.25rem;
  }
`;

const CreateBoardCard = styled(BoardCard)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background-color: rgba(0, 122, 255, 0.05);  /* Very light blue */
  border: 1px solid rgba(0, 122, 255, 0.1);  /* Light blue border */
  min-height: 150px;
  
  &:hover {
    background-color: rgba(0, 122, 255, 0.08);
    border-color: rgba(0, 122, 255, 0.15);
  }
  
  svg {
    width: 2.5rem;
    height: 2.5rem;
    color: #007AFF;  /* Apple blue */
    margin-bottom: 1rem;
  }
  
  span {
    font-weight: 500;
    color: #007AFF;  /* Apple blue */
  }
`;

const Button = styled.button`
  background-color: ${props => props.variant === 'outline' ? 'transparent' : props.color || '#007AFF'};  /* Apple blue */
  color: ${props => props.variant === 'outline' ? (props.color || '#007AFF') : 'white'};
  border: ${props => props.variant === 'outline' ? `1px solid ${props.color || '#007AFF'}` : 'none'};
  border-radius: 8px;
  padding: 0.6rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);  /* Smooth animation */
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover {
    background-color: ${props => props.variant === 'outline' 
      ? 'rgba(0, 122, 255, 0.05)' 
      : (props.color ? props.colorHover || props.color : '#0062CC')};  /* Darker blue */
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const AddButton = styled(Button)`
  width: 100%;
  margin-top: 0.5rem;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 2rem;
  width: 450px;
  max-width: 90vw;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
`;

const ModalTitle = styled.h2`
  margin-top: 0;
  margin-bottom: 1.5rem;
  font-size: 1.3rem;
  color: #1D1D1F;
  font-weight: 600;
  letter-spacing: -0.02em;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #1D1D1F;
  font-size: 0.875rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #007AFF;  /* Apple blue */
    box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.15);
  }
  
  &::placeholder {
    color: #86868B;  /* Apple secondary text color */
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
  gap: 1rem;
`;

const Divider = styled.div`
  height: 1px;
  background-color: rgba(0, 0, 0, 0.1);
  margin: 2rem 0;
`;

const SpeechContainer = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  margin-top: 2rem;
  border: 1px solid rgba(0, 0, 0, 0.03);
`;

const AuthSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

// SVG Icons as components
const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const TaskIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 11l3 3L22 4"></path>
    <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"></path>
  </svg>
);

const TaskManager = ({ authState = { isSignedIn: true, user: null } }) => {
  const [boards, setBoards] = useState([]);
  const [showAddBoardModal, setShowAddBoardModal] = useState(false);
  const [newBoardTitle, setNewBoardTitle] = useState('');
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [showPersonalBoard, setShowPersonalBoard] = useState(false);
  
  const { isSignedIn, user } = authState;
  
  // Check if Clerk is available for UI components using secure loader
  const clerkConfig = getClerkConfig();
  const isClerkAvailable = clerkConfig?.available && clerkConfig?.publishableKey;
  const [storageMode, setStorageMode] = useState('local');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load boards on component mount
  useEffect(() => {
    const initializeService = async () => {
      try {
        // Initialize the service and test backend connection
        const backendConnected = await taskService.initialize();
        
        if (!backendConnected) {
          console.warn('Backend connection failed. Operating in local-only mode.');
          setStorageMode('local');
          
          // Try to load any existing local data
          try {
            const localBoards = JSON.parse(localStorage.getItem('taskManager_boards') || '[]');
            setBoards(localBoards);
            setError('Backend unavailable. Working with local data only. Your data is saved locally.');
          } catch (localError) {
            console.warn('No local data available:', localError);
            setBoards([]);
            setError('Backend unavailable and no local data found. You can still create boards - they will be saved locally.');
          }
          setLoading(false);
          return;
        }
        
        // Backend is available, try to fetch boards
        fetchBoards();
      } catch (error) {
        console.error('Error initializing TaskManager:', error);
        
        // Fallback to local mode
        setStorageMode('local');
        try {
          const localBoards = JSON.parse(localStorage.getItem('taskManager_boards') || '[]');
          setBoards(localBoards);
          setError('Connection failed. Working with local data only.');
        } catch (localError) {
          setBoards([]);
          setError('Connection failed. You can still create boards - they will be saved locally.');
        }
        setLoading(false);
      }
    };
    
    initializeService();
  }, []);

  const fetchBoards = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all boards
      const fetchedBoards = await taskService.getBoards();
      
      // Ensure we have an array of boards
      if (!Array.isArray(fetchedBoards)) {
        console.error('Boards response is not an array:', fetchedBoards);
        
        // Fallback to local storage
        try {
          const localBoards = JSON.parse(localStorage.getItem('taskManager_boards') || '[]');
          setBoards(localBoards);
          setError('Invalid server response. Loaded local data instead.');
          setStorageMode('local');
        } catch (localError) {
          setBoards([]);
          setError('Invalid server response and no local data. Create a new board to start.');
        }
      } else {
        console.log(`Fetched ${fetchedBoards.length} boards`);
        setBoards(fetchedBoards);
        setStorageMode(taskService.getStorageMode());
      }
    } catch (err) {
      console.error('Error fetching boards:', err);
      
      // Fallback to local storage when backend fails
      try {
        const localBoards = JSON.parse(localStorage.getItem('taskManager_boards') || '[]');
        setBoards(localBoards);
        setError('Backend connection failed. Working with local data only.');
        setStorageMode('local');
      } catch (localError) {
        setBoards([]);
               setError('Backend connection failed and no local data available. Create a new board to start.');
         setStorageMode('local');
       }
      setBoards([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBoard = async () => {
    if (!newBoardTitle.trim()) return;
    
    // Create a new board with default columns according to API guide
    const newBoard = {
      title: newBoardTitle,
      description: `Board created on ${new Date().toLocaleDateString()}`,
      columns: [
        {
          id: 'todo',
          title: 'To Do',
          taskIds: []
        },
        {
          id: 'in-progress',
          title: 'In Progress',
          taskIds: []
        },
        {
          id: 'completed',
          title: 'Completed',
          taskIds: []
        }
      ]
    };
    
    try {
      setLoading(true);
      
      // Create the board using the task service
      const addedBoard = await taskService.createBoard(newBoard);
      
      if (!addedBoard || (!addedBoard.id && !addedBoard._id)) {
        console.error('Created board has no ID:', addedBoard);
        setError('Failed to create board properly.');
        setLoading(false);
        return;
      }
      
      console.log('Board created successfully:', addedBoard);
      
      // Add to the boards list
      setBoards(prevBoards => [...prevBoards, addedBoard]);
      
      // Reset form
      setNewBoardTitle('');
      setShowAddBoardModal(false);
    } catch (error) {
      console.error("Error adding board:", error);
      setError("Failed to add board. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenBoard = (boardId) => {
    console.log(`Opening board with ID: ${boardId}`);
    
    // Find the board in our list
    const board = boards.find(b => {
      // Handle both MongoDB _id and regular id fields
      const boardsId = b.id || b._id;
      return boardsId === boardId;
    });
    
    if (board) {
      console.log('Found board:', board);
      
      // Ensure the board has columns with taskIds
      const processedBoard = { ...board };
      
      if (!Array.isArray(processedBoard.columns)) {
        processedBoard.columns = [];
      }
      
      // Ensure each column has taskIds array
      processedBoard.columns = processedBoard.columns.map(column => ({
        ...column,
        taskIds: Array.isArray(column.taskIds) ? column.taskIds : []
      }));
      
      setSelectedBoard(processedBoard);
    } else {
      console.error(`Board not found with ID: ${boardId}`);
      setError(`Board with ID ${boardId} was not found.`);
    }
  };

  const handleBackToBoardList = () => {
    setSelectedBoard(null);
    
    // Refresh the boards list when returning to the board list
    fetchBoards();
  };

  const handleUpdateBoard = async (updatedBoard) => {
    try {
      // Ensure the board has the expected structure before updating
      const boardToUpdate = { ...updatedBoard };
      
      // Ensure columns have taskIds arrays
      if (Array.isArray(boardToUpdate.columns)) {
        boardToUpdate.columns = boardToUpdate.columns.map(column => ({
          ...column,
          taskIds: Array.isArray(column.taskIds) ? column.taskIds : []
        }));
      }
      
      // Get the board ID
      const boardId = boardToUpdate.id || boardToUpdate._id;
      if (!boardId) {
        console.error('Board has no ID:', boardToUpdate);
        throw new Error('Board has no ID');
      }
      
      // Update the board
      await taskService.updateBoard(boardId, boardToUpdate);
      
      // Fetch the updated board to ensure we have the latest data including tasks
      const refreshedBoard = await taskService.getBoard(boardId);
      
      // If we got a valid board back, use it instead of our local copy
      if (refreshedBoard && refreshedBoard.columns) {
        console.log('Retrieved updated board with tasks:', refreshedBoard);
        
        // Update boards list with the refreshed board
        setBoards(prevBoards => 
          prevBoards.map(board => 
            ((board.id || board._id) === boardId) ? refreshedBoard : board
          )
        );
        
        // Update selected board if it's the one that was updated
        if (selectedBoard && ((selectedBoard.id || selectedBoard._id) === boardId)) {
          setSelectedBoard(refreshedBoard);
        }
      } else {
        // If we couldn't get a refreshed board, just update with our local copy
        setBoards(prevBoards => 
          prevBoards.map(board => 
            ((board.id || board._id) === boardId) ? boardToUpdate : board
          )
        );
        
        // Update selected board if it's the one that was updated
        if (selectedBoard && ((selectedBoard.id || selectedBoard._id) === boardId)) {
          setSelectedBoard(boardToUpdate);
        }
      }
      
      console.log(`Board ${boardId} updated successfully`);
    } catch (error) {
      console.error("Error updating board:", error);
      setError("Failed to update board. Changes may not be saved.");
    }
  };

  const extractColorFromText = (text) => {
    const colorRegex = /\b(blue|red|green|yellow|purple|teal|orange|pink|gray)\b/i;
    const match = text.match(colorRegex);
    return match ? match[0].toLowerCase() : getRandomColorKey();
  };

  const handleAddTaskFromSpeech = (taskText, transcript) => {
    if (selectedBoard) {
      // Get the first column ID from the board
      const firstColumn = selectedBoard.columns[0];
      if (firstColumn) {
        const columnId = firstColumn.id;
        
        // Extract color from the transcript or use a random one
        const color = extractColorFromText(transcript) || getRandomColorKey();
        
        // Create a new task object
        const newTask = {
          text: taskText,
          color,
          boardId: selectedBoard.id || selectedBoard._id,
          columnId,
          userId: taskService.getUserId()
        };
        
        // Update the board with the new task
        const updatedBoard = { ...selectedBoard };
        
        // Pass the updated board to the parent component
        handleUpdateBoard(updatedBoard);
      }
    }
  };

  if (loading) {
    return (
      <TaskManagerContainer>
        <Header>
          <Title>Task Manager</Title>
        </Header>
        <MainContent>
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <div className="loading-spinner"></div>
            <p>Loading your boards...</p>
            <p>Connecting to backend...</p>
          </div>
        </MainContent>
      </TaskManagerContainer>
    );
  }

  if (error) {
    return (
      <TaskManagerContainer>
        <Header>
          <Title>Task Manager</Title>
        </Header>
        <MainContent>
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <h2>Connection Error</h2>
            <p>{error}</p>
            <Button onClick={() => window.location.reload()}>Retry Connection</Button>
          </div>
        </MainContent>
      </TaskManagerContainer>
    );
  }

  // Show personal board if selected
  if (showPersonalBoard) {
    return <PersonalBoard onBack={() => setShowPersonalBoard(false)} user={user} />;
  }

  // Show board list if no board is selected
  if (!selectedBoard) {
    return (
      <TaskManagerContainer>
        <BackendStatus />
        
        <Header>
          <Title>Task Manager</Title>
          <AuthSection>
            {/* Only render UserButton if Clerk is available */}
            {isClerkAvailable && <UserButton />}
          </AuthSection>
        </Header>
        
        {/* Show friendly notice when working in local mode */}
        {storageMode === 'local' && error && (
          <BackendOfflineNotice message={error} />
        )}
        
        <MainContent>
          <SectionTitle>Your Boards</SectionTitle>
          
          {/* Personal Tasks Button */}
          {user && (
            <PersonalTasksButton onClick={() => setShowPersonalBoard(true)}>
              <TaskIcon />
              My Assigned Tasks
            </PersonalTasksButton>
          )}
          
          <BoardsContainer>
            {boards.map(board => (
              <BoardCard 
                key={board.id || board._id} 
                onClick={() => handleOpenBoard(board.id || board._id)}
              >
                <BoardTitle>{board.title || 'Untitled Board'}</BoardTitle>
                <BoardStats>
                  <StatItem>
                    <TaskIcon />
                    {board.taskCount !== undefined 
                      ? `${board.taskCount} task${board.taskCount !== 1 ? 's' : ''}` 
                      : board.tasks && Array.isArray(board.tasks) 
                        ? `${board.tasks.length} task${board.tasks.length !== 1 ? 's' : ''}`
                        : ''}
                  </StatItem>
                </BoardStats>
                <Button variant="outline">Open Board</Button>
              </BoardCard>
            ))}
            
            <CreateBoardCard onClick={() => setShowAddBoardModal(true)}>
              <PlusIcon />
              <span>Create New Board</span>
            </CreateBoardCard>
          </BoardsContainer>
        </MainContent>

        {/* Add Board Modal */}
        {showAddBoardModal && (
          <Modal>
            <ModalContent>
              <ModalTitle>Create New Board</ModalTitle>
              <FormGroup>
                <Label htmlFor="boardTitle">Board Title</Label>
                <Input
                  id="boardTitle"
                  type="text"
                  placeholder="Enter board title"
                  value={newBoardTitle}
                  onChange={(e) => setNewBoardTitle(e.target.value)}
                />
              </FormGroup>
              <ButtonGroup>
                <Button 
                  variant="outline" 
                  onClick={() => setShowAddBoardModal(false)}
                >
                  Cancel
                </Button>
                <Button onClick={handleAddBoard}>Create Board</Button>
              </ButtonGroup>
            </ModalContent>
          </Modal>
        )}
      </TaskManagerContainer>
    );
  }

  // Show selected board
  return (
    <>
      <BackendStatus />
      <BoardView
        board={selectedBoard}
        onBack={handleBackToBoardList}
        onUpdateBoard={handleUpdateBoard}
      />
    </>
  );
};

const PersonalTasksButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s;
  margin-bottom: 20px;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

export default TaskManager; 